import ApiService from "@/api";
import EnumProjectType from "@/api/Main/dataSource/api/projects/base/entities/enums/EnumProjectType";
import ProjectSearchRequest from "@/api/Main/dataSource/api/projects/base/i-service/models/ProjectSearchRequest";
import GetStatusesListDetailRequest from "@/api/Main/dataSource/api/projects/task/i-service/models/status/GetStatusesListDetailRequest";
import { mapGetTreeResponse } from "@/components/sidebar/adapter/AdapterServiceMappers";
import { NamespaceModel } from "@/components/sidebar/models";
import { TaskParameterModel } from "@/components/task-parameter/models";
import { ProjectModel, ResponseModel } from "@/models";
import { EntityParametersModel, ParametersTemplateModel } from "../../models";
import { TaskParameterTypeEnum } from "@/components";
import GetPrioritiesListDetailRequest from "@/api/Main/dataSource/api/projects/task/i-service/models/type-priority/GetPrioritiesListDetailRequest";
import { TaskTypesService } from "./TaskTypesService";
import { TaskTagsService } from "./TaskTagsService";
import { TaskDifficultiesService } from "./TaskDifficultiesService";
import { SendErrorNotification } from "../functions";

/**Сервис для получения информации о проектах */
export class ProjectsService {
  /**Сервис для использования API */
  api = new ApiService();
  /**Сервис для получения типов */
  typesService = new TaskTypesService();
  /**Сервис для получения сложностей */
  difficultiesService = new TaskDifficultiesService();
  /**Сервис для получения тегов */
  tagsService = new TaskTagsService();

  /**Получить список проектов */
  GetProjects(): Promise<ResponseModel<ProjectModel[]>> {
    return new Promise<ResponseModel<ProjectModel[]>>(async (resolve) => {
      const result = new ResponseModel<ProjectModel[]>();

      await this.api.Main.ProjectBaseService.searchAsync(
        new ProjectSearchRequest({
          Status: [EnumProjectType.Active],
        }),
      )
        .then((response) => {
          result.Value = response.Projects.map((x) => new ProjectModel(x as any));
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          SendErrorNotification(result.ErrorMessage);
          console.error("ERROR ProjectBaseService.Search", e);
          return resolve(result);
        });
    });
  }
  /**Получение параметров сущности */
  GetEntityParameters(
    type: TaskParameterTypeEnum,
    search: string,
    projectId?: number,
    namespaceId?: string,
    groupId?: number,
    queueId?: string,
  ): Promise<ResponseModel<ParametersTemplateModel[]>> {
    return new Promise<ResponseModel<ParametersTemplateModel[]>>(async (resolve) => {
      switch (type) {
        case TaskParameterTypeEnum.Status:
          return resolve(this.GetStatuses(search, namespaceId, groupId, queueId));
        case TaskParameterTypeEnum.Priority:
          return resolve(this.GetPriorities(search, namespaceId, groupId, queueId));
        case TaskParameterTypeEnum.Type:
          const typesResult = await this.typesService.GetList(namespaceId);
          return resolve(this.GetTemplateResult(typesResult));
        case TaskParameterTypeEnum.Difficulty:
          const difficultiesResult = await this.difficultiesService.GetList(namespaceId);
          return resolve(this.GetTemplateResult(difficultiesResult));
        case TaskParameterTypeEnum.Tag:
          const tagsResult = await this.tagsService.GetList(projectId);
          return resolve(this.GetTemplateResult(tagsResult));
      }
    });
  }
  /**Получение параметров в виде шаблона для импорта */
  GetTemplateResult(response: ResponseModel<TaskParameterModel[]>) {
    const result = new ResponseModel<ParametersTemplateModel[]>();
    result.Value = [
      new ParametersTemplateModel({
        Name: "",
        Data: JSON.stringify(response.Value),
      }),
    ];
    return result;
  }
  /**Получение статусов очередей */
  GetStatuses(
    search: string,
    namespaceId: string,
    groupId?: number,
    queueId?: string,
  ): Promise<ResponseModel<ParametersTemplateModel[]>> {
    return new Promise<ResponseModel<ParametersTemplateModel[]>>(async (resolve) => {
      const result = new ResponseModel<ParametersTemplateModel[]>();

      await this.api.Main.TaskStatusListService.getStatusesListDetailAsync(
        new GetStatusesListDetailRequest({
          Search: search,
          QueueTaskId: queueId,
          NamespaceId: namespaceId,
          GroupId: groupId,
        }),
      )
        .then((response) => {
          result.Value = response.Items.map(
            (x) =>
              new ParametersTemplateModel({
                Id: x.QueueTaskId,
                Name: x.QueueTaskName,
                Type: TaskParameterTypeEnum.Status,
                // Data: JSON.stringify(
                //   x.Statuses.map(
                //     (s) =>
                //       new TaskParameterModel({
                //         Id: s.Id,
                //         Color: s.Color,
                //         Name: s.Name,
                //         IsDefault: s.IsDefault,
                //         InUse: false,
                //       }),
                //   ),
                // ),
              }),
          );
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          SendErrorNotification(result.ErrorMessage);
          console.error("ERROR GetStatuses", e);
          return resolve(result);
        });
    });
  }
  /**Получение приоритетов очередей */
  GetPriorities(
    search: string,
    namespaceId: string,
    groupId?: number,
    queueId?: string,
  ): Promise<ResponseModel<ParametersTemplateModel[]>> {
    return new Promise<ResponseModel<ParametersTemplateModel[]>>(async (resolve) => {
      const result = new ResponseModel<ParametersTemplateModel[]>();

      await this.api.Main.TaskPriorityGetService.getPrioritiesListDetailAsync(
        new GetPrioritiesListDetailRequest({
          Search: search,
          QueueTaskId: queueId,
          NamespaceId: namespaceId,
          GroupId: groupId,
        }),
      )
        .then((response) => {
          result.Value = response.Items.map(
            (x) =>
              new ParametersTemplateModel({
                Id: x.QueueTaskId,
                Name: x.QueueTaskName,
                Type: TaskParameterTypeEnum.Priority,
                // Data: JSON.stringify(
                //   x.Priorities.map(
                //     (s) =>
                //       new TaskParameterModel({
                //         Id: s.Id,
                //         Color: s.Color,
                //         Name: s.Name,
                //         IsDefault: s.IsDefault,
                //         InUse: false,
                //       }),
                //   ),
                // ),
              }),
          );
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          SendErrorNotification(result.ErrorMessage);
          console.error("ERROR GetPriorities", e);
          return resolve(result);
        });
    });
  }
}
